import React, { useEffect, useState } from 'react';
import { fetchEmployeesList, deleteEmployee, fetchPortsList, fetchDesignationsList } from './services/api';
import { Employee, Designation, Port } from './types';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Button, Image, Form, Row, Col } from 'react-bootstrap';
import { useAuth } from './AuthContext';

const EmployeeList: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [ports, setPorts] = useState<Port[]>([]);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [filterPort, setFilterPort] = useState('');
  const [filterDesignation, setFilterDesignation] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [search, setSearch] = useState('');
  const [filtersApplied, setFiltersApplied] = useState(false);
  const navigate = useNavigate();
  const { token } = useAuth();

  useEffect(() => {
    fetchPortsList()
      .then(response => {
        const ports = Array.isArray(response.data) ? response.data : [];
        setPorts(ports);
      })
      .catch(() => setPorts([]));

    fetchDesignationsList()
      .then(response => {
        const designations = Array.isArray(response.data) ? response.data : [];
        setDesignations(designations);
      })
      .catch(() => setDesignations([]));

    if (token && token.designation?.user_type === 'ADMIN' && token.port) {
      setFilterPort(String(token.port));
    }
    if (token && token.designation?.user_type === 'ADMIN') {
      setSearch(token.employee_code || '');
    }
  }, [token]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to deactivate this employee?')) return;
    setDeletingId(id);
    try {
      // Find the employee in the current list to pass to deleteEmployee
      const employee = employees.find(e => e.id === id);
      await deleteEmployee(id, employee);
      // Update the employee's is_active status instead of removing it
      setEmployees(es => es.map(e => e.id === id ? { ...e, is_active: false } : e));
    } catch {
      alert('Failed to deactivate employee');
    } finally {
      setDeletingId(null);
    }
  };

  const handleApplyFilters = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);
    setFiltersApplied(true);
    try {
      const response = await fetchEmployeesList();
      const employees = Array.isArray(response.data) ? response.data : [];
      setEmployees(employees);
    } catch {
      setError('Failed to load employees');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter(e => {
    const matchesPort = filterPort ? String(e.port) === filterPort : true;
    const matchesDesignation = filterDesignation ? String(e.designation?.id) === filterDesignation : true;
    const matchesActive = filterActive === '' ? true : filterActive === 'true' ? (e.is_active === true) : (e.is_active === false);
    const matchesSearch = search
      ? (e.name.toLowerCase().includes(search.toLowerCase()) || e.employee_code.toLowerCase().includes(search.toLowerCase()))
      : true;
    return matchesPort && matchesDesignation && matchesActive && matchesSearch;
  });

  return (
    <div className="p-4 p-md-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Employees</h2>
        <Button variant="primary" onClick={() => navigate('/employees/create')}>
          <i className="fas fa-plus me-2"></i> Create Employee
        </Button>
      </div>
      <Form className="mb-3" onSubmit={handleApplyFilters}>
        <Row className="g-2 align-items-end">
          <Col md={3}>
            <Form.Group controlId="filterPort">
              <Form.Label>Location</Form.Label>
              <Form.Select value={filterPort} onChange={e => setFilterPort(e.target.value)} disabled={!!(token && token.designation?.user_type === 'ADMIN' && token.port)}>
                <option value="">All Locations</option>
                {ports.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="filterDesignation">
              <Form.Label>Designation</Form.Label>
              <Form.Select value={filterDesignation} onChange={e => setFilterDesignation(e.target.value)}>
                <option value="">All Designations</option>
                {designations.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group controlId="filterActive">
              <Form.Label>Status</Form.Label>
              <Form.Select value={filterActive} onChange={e => setFilterActive(e.target.value)}>
                <option value="">All Employees</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group controlId="search">
              <Form.Label>Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search by name or code"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={2} className="d-grid">
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? <span className="spinner-border spinner-border-sm" /> : 'Apply Filters'}
            </Button>
          </Col>
        </Row>
      </Form>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <th>Profile</th>
              <th>Name</th>
              <th>Code</th>
              <th>Mobile</th>
              <th>Gender</th>
              <th>Location</th>
              <th>Designation</th>
              <th>Status</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td><Skeleton circle width={40} height={40} /></td>
                  <td><Skeleton width={120} /></td>
                  <td><Skeleton width={80} /></td>
                  <td><Skeleton width={100} /></td>
                  <td><Skeleton width={60} /></td>
                  <td><Skeleton width={100} /></td>
                  <td><Skeleton width={100} /></td>
                  <td><Skeleton width={80} /></td>
                  <td><Skeleton width={70} /></td>
                </tr>
              ))
            ) : !filtersApplied ? (
              <tr><td colSpan={9} className="text-center text-muted">No data. Please apply filters.</td></tr>
            ) : filteredEmployees.length === 0 ? (
              <tr><td colSpan={9} className="text-center">No employees found.</td></tr>
            ) : (
              filteredEmployees.map(e => (
                <tr key={e.id}>
                  <td>
                    {e.profile_image ? (
                      <Image src={e.profile_image} roundedCircle width={40} height={40} alt='' style={{ objectFit: 'cover' }} />
                    ) : (
                      <div className="bg-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                        <i className="fas fa-user text-secondary" style={{ fontSize: '1.2rem' }}></i>
                      </div>
                    )}
                  </td>
                  <td>{e.name}</td>
                  <td>{e.employee_code}</td>
                  <td>{e.mobile_number}</td>
                  <td>{e.gender}</td>
                  <td>{e.port_name}</td>
                  <td>{e.designation?.name}</td>
                  <td>
                    <span className={`badge ${e.is_active !== false ? 'bg-success' : 'bg-secondary'}`}>
                      {e.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => navigate(`/employees/edit/${e.id}`)}>
                      <i className="fas fa-edit"></i>
                    </button>
                    {e.is_active !== false && <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(e.id)} disabled={deletingId === e.id}>
                      {deletingId === e.id ? <span className="spinner-border spinner-border-sm" /> : <i className="fas fa-trash"></i>}
                    </button>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeeList; 